import { Check, ChevronDown, FileUp, Languages, MegaphoneOff, Type, Wallpaper } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router";
import { LANGUAGES } from "~/i18n";
import cn from "~/utils/cn";
import Button from "../ui/Button";
import Logo from "../ui/Logo";
import useDesign from "../useDesign";

// Entrance stagger runs from CSS so the form is visible on first paint, before hydration. Reduced motion skips it.
const CSS = `
@media (prefers-reduced-motion: no-preference) {
  .auth-in { animation: auth-rise .7s var(--ease-out) both; animation-delay: calc(var(--i, 0) * 70ms + 40ms); }
  .auth-pop { animation: auth-pop .9s var(--ease-spring) both; animation-delay: calc(var(--i, 0) * 110ms + 350ms); }
}
@keyframes auth-rise { from { opacity: 0; transform: translateY(14px); } }
@keyframes auth-pop { from { opacity: 0; transform: scale(.55) rotate(-14deg); } }
`;

const FEATURES = [
  { key: "fonts", Icon: Type },
  { key: "backgrounds", Icon: Wallpaper },
  { key: "import", Icon: FileUp },
  { key: "ads", Icon: MegaphoneOff },
] as const;

// Shared frame for /login, /register and /forgot: per-design atmosphere, a 400px form column,
// and from lg up a Pro pitch panel beside it so the desktop page is not empty.
export default function AuthLayout({ title, subtitle, children, alt }: { title: ReactNode; subtitle?: ReactNode; children: ReactNode; alt?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="relative isolate flex min-h-[100dvh] flex-col overflow-x-clip px-[var(--gutter)] pb-[calc(env(safe-area-inset-bottom)+12px)] pt-[env(safe-area-inset-top)]">
      <style dangerouslySetInnerHTML={{ __html: CSS }} />
      <Atmosphere />
      <div className="mx-auto flex w-full max-w-6xl flex-1 items-stretch gap-16 xl:gap-24">
        <div className="mx-auto flex w-full max-w-[400px] flex-col lg:mx-0 lg:shrink-0">
          <header className="auth-in flex h-16 items-center">
            <Logo size={34} />
          </header>
          <div className="flex flex-1 flex-col lg:justify-center lg:pt-6">
            {/* mobile: short pages (forgot) center in the free space and the switch sits at the bottom */}
            <main className="flex flex-1 flex-col justify-center pb-6 pt-2 lg:flex-none lg:py-0">
              <h1 className="auth-in font-display text-[1.75rem] font-black leading-[1.35] text-ink" style={{ "--i": 1 } as React.CSSProperties}>
                {title}
              </h1>
              {subtitle && (
                <p className="auth-in mt-2 text-[15px] leading-7 text-ink-2" style={{ "--i": 2 } as React.CSSProperties}>
                  {subtitle}
                </p>
              )}
              <div className="auth-in mt-6 lg:mt-8" style={{ "--i": 3 } as React.CSSProperties}>
                {children}
              </div>
            </main>
            {alt && (
              <div className="auth-in lg:mt-6" style={{ "--i": 4 } as React.CSSProperties}>
                {alt}
              </div>
            )}
          </div>
          <footer className="flex items-center justify-center gap-1 pt-4 text-sm">
            <Link to="/" prefetch="intent" className="inline-flex min-h-11 items-center rounded-btn px-3 font-medium text-ink-2 transition-colors hover:bg-accent-soft hover:text-ink">
              {t("ui.auth.footer.home")}
            </Link>
            <span className="h-4 w-px bg-line-2" aria-hidden />
            <LanguageMenu />
          </footer>
        </div>
        <Pitch />
      </div>
    </div>
  );
}

// "No account yet?" + a secondary button to the other auth page; an inline link on desktop where height is tight.
export function AuthSwitch({ text, cta, to }: { text: ReactNode; cta: ReactNode; to: string }) {
  return (
    <div className="flex flex-col gap-3 text-center lg:flex-row lg:items-center lg:justify-center lg:gap-1">
      <p className="text-[15px] text-ink-2">{text}</p>
      <Button
        variant="secondary"
        size="lg"
        block
        to={to}
        prefetch="intent"
        className="lg:h-11 lg:w-auto lg:border-transparent lg:bg-transparent lg:px-2 lg:text-[15px] lg:text-accent-text lg:underline-offset-[6px] lg:hover:border-transparent lg:hover:underline"
      >
        {cta}
      </Button>
    </div>
  );
}

function Pitch() {
  const { t } = useTranslation();
  const design = useDesign();
  return (
    <aside className="hidden flex-1 items-center py-16 lg:flex" aria-labelledby="auth-pitch">
      <div
        className={cn(
          "auth-in relative mx-auto w-full max-w-[500px] rounded-lg border border-line p-10 xl:p-12",
          design === "a" && "bg-[color-mix(in_srgb,var(--c-surface)_45%,transparent)] shadow-card backdrop-blur-xl",
          design === "b" && "bg-surface shadow-card",
          design === "c" && "bg-surface shadow-card backdrop-blur-md",
        )}
        style={{ "--i": 2 } as React.CSSProperties}
      >
        {design === "c" && <Stickers desktop />}
        <h2 id="auth-pitch" className="text-balance font-display text-[2.5rem] font-black leading-[1.3] text-ink">
          {t("ui.auth.pitch.title")}
        </h2>
        <p className="mt-3 text-base leading-8 text-ink-2">{t("ui.auth.pitch.body")}</p>
        <ul className={cn("mt-9 flex flex-col", design === "b" ? "divide-y divide-line border-y border-line" : "gap-4")}>
          {FEATURES.map(({ key, Icon }) => (
            <li key={key} className={cn("flex items-center gap-4", design === "b" && "py-4")}>
              <span className="grid size-11 shrink-0 place-items-center rounded-sm bg-accent-soft text-accent-text">
                <Icon className="size-[22px]" strokeWidth={1.75} aria-hidden />
              </span>
              <span className="font-display text-lg font-bold text-ink">{t(`ui.landing.features.items.${key}.title`)}</span>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

function Atmosphere() {
  const design = useDesign();
  return (
    <div className="pointer-events-none absolute inset-0 -z-10 select-none overflow-hidden" aria-hidden>
      {design === "a" && (
        <>
          <span className="absolute -end-[4%] top-[1%] font-display text-[min(66vw,600px)] font-black leading-none text-[color-mix(in_srgb,var(--c-accent)_12%,var(--c-bg))] lg:end-[8%] lg:top-[-4%]">
            ف
          </span>
          <span className="absolute -bottom-[4%] -start-[4%] font-display text-[min(46vw,240px)] font-extrabold leading-none text-[color-mix(in_srgb,var(--c-accent)_9%,var(--c-bg))] lg:bottom-auto lg:start-auto lg:end-[2%] lg:top-[34%]">
            ت
          </span>
        </>
      )}
      {design === "b" && (
        // solid tint, not a stroke or alpha: both expose the overlapping joins between Persian glyphs as stray lines.
        // Smaller and lower on desktop so it stays under the form instead of behind its links.
        <span className="absolute inset-x-0 -bottom-[0.32em] text-center font-display text-[clamp(7.5rem,36vw,24rem)] lg:-bottom-[0.66em] lg:text-[18rem] font-black leading-none text-[color-mix(in_srgb,var(--c-accent)_9%,var(--c-bg))]">
          فونتا
        </span>
      )}
      {design === "c" && <Stickers />}
    </div>
  );
}

// Story-text stickers. On mobile two sit in the header row; on desktop four hug the pitch card's corners.
function Stickers({ desktop = false }: { desktop?: boolean }) {
  const items = [
    { text: "سلام!", cls: "font-alt text-[2rem] font-extrabold", r: -9 },
    { text: "امروز", cls: "rounded-md bg-ink px-[0.45em] font-display text-xl font-extrabold text-on-accent", r: 7 },
    { text: "حس خوب", cls: "font-alt text-[2.75rem] font-light", r: -5 },
    { text: "جمعه‌ها", cls: "rounded-md bg-[rgba(11,17,77,0.78)] px-[0.5em] font-display text-lg font-black", r: 9 },
  ];
  const spots = desktop
    ? ["-top-7 start-[52%]", "-top-4 -end-5", "-bottom-9 end-[10%]", "-bottom-4 start-[30%]"]
    : ["top-[18px] end-[34%] lg:hidden", "top-[22px] end-[6%] lg:hidden"];
  return (
    <div className={cn("pointer-events-none", desktop ? "absolute inset-0" : "contents")} aria-hidden>
      {spots.map((spot, i) => (
        <span key={i} className={cn("absolute z-[1] whitespace-nowrap leading-[1.4] text-ink", spot)}>
          <span className={cn("auth-pop inline-block", items[i].cls)} style={{ "--i": i, rotate: `${items[i].r}deg` } as React.CSSProperties}>
            {items[i].text}
          </span>
        </span>
      ))}
    </div>
  );
}

// Disclosure list of languages (one today). Links keep the current page and switch through ?lng=.
function LanguageMenu() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const current = i18n.language?.toLowerCase();
  const name = (lng: string, fallback: string) => t(`ui.auth.languages.${lng}`, { defaultValue: fallback });

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      button.current?.focus();
    };
    document.addEventListener("pointerdown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const active = LANGUAGES.find((l) => l.lng === current) ?? LANGUAGES[0];
  return (
    <div ref={root} className="relative">
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls="auth-languages"
        aria-label={`${t("ui.auth.footer.language")}: ${name(active.lng, active.name)}`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-11 items-center gap-1.5 rounded-btn px-3 font-medium text-ink-2 transition-colors hover:bg-accent-soft hover:text-ink"
      >
        <Languages className="size-[18px]" strokeWidth={1.75} aria-hidden />
        {name(active.lng, active.name)}
        <ChevronDown className={cn("size-4 transition-transform duration-200", open && "rotate-180")} strokeWidth={1.75} aria-hidden />
      </button>
      {open && (
        <ul id="auth-languages" className="absolute bottom-full end-0 z-overlay mb-2 min-w-44 rounded-md border border-line bg-raised p-1.5 shadow-card">
          {LANGUAGES.map(({ lng, name: fallback }) => (
            <li key={lng}>
              <a
                href={`${location.pathname}?lng=${lng}`}
                lang={lng}
                aria-current={lng === active.lng ? "true" : undefined}
                className="flex min-h-11 items-center justify-between gap-3 rounded-sm px-3 text-ink transition-colors hover:bg-accent-soft"
              >
                {name(lng, fallback)}
                {lng === active.lng && <Check className="size-[18px] text-accent-text" strokeWidth={1.75} aria-hidden />}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
